import React, { useMemo, useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import Select from 'react-select';
import { queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import { getTemplateFolders, cloneTargetTeams } from '../../../api/queries_v2';
import { FolderTreeSelector } from '../../../components/folder-tree-selector';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { ToggleField, FormSection } from '../../../components/shared/form-components';
import { AddNewTemplateRestrictedEditing } from '../../../consts/forms-constants';
import { paths } from '../../../routing';
import { cloneDocumentTemplate } from '../../../api/mutations';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

type CloneTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  templateType: 'template' | 'clause';
  sourceTeamId: string;
  /** Source template's restricted-editing state — seeds the checkbox default. */
  sourceIsRestricted?: boolean;
  /** Restricted editing is a docx-native feature; only show the checkbox there. */
  sourceIsDocxNative?: boolean;
  onSuccess: () => void;
};

const CloneTemplateModal = ({
  isOpen,
  onClose,
  templateId,
  templateName,
  templateType,
  sourceTeamId,
  sourceIsRestricted = false,
  sourceIsDocxNative = false,
  onSuccess,
}: CloneTemplateModalProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [name, setName] = useState(`Copy of: ${templateName}`);
  const [targetTeamId, setTargetTeamId] = useState(sourceTeamId);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [restrictedEditing, setRestrictedEditing] = useState(sourceIsRestricted);
  const [isLoading, setIsLoading] = useState(false);

  // Reset name when template changes
  useEffect(() => {
    setName(`Copy of: ${templateName}`);
  }, [templateName]);

  // Reset state when modal opens/closes — the restricted checkbox defaults to
  // the source template's current setting.
  useEffect(() => {
    if (isOpen) {
      setName(`Copy of: ${templateName}`);
      setTargetTeamId(sourceTeamId);
      setSelectedFolderId(null);
      setRestrictedEditing(sourceIsRestricted);
    }
  }, [isOpen, templateName, sourceTeamId, sourceIsRestricted]);

  // Fetch ALL teams with same owner (no permission filtering)
  const { data: teamsData, loading: teamsLoading } = useQuery(cloneTargetTeams, {
    variables: { sourceTeamId },
    skip: !isOpen,
  });

  // Fetch folders for selected target team
  const { data: foldersData, loading: foldersLoading } = useQuery(getTemplateFolders as any, {
    variables: {
      teamId: targetTeamId,
      folderType: templateType === 'clause' ? 'clause' : 'template',
    },
    skip: !isOpen || !targetTeamId,
  });

  // Clone mutation
  const [cloneTemplate] = useMutation(cloneDocumentTemplate, {
    onCompleted: (data) => {
      const clonedId = data.cloneDocumentTemplate.id;
      const clonedTeamId = data.cloneDocumentTemplate.team.id;

      setIsLoading(false);

      if (clonedTeamId !== sourceTeamId) {
        // Navigate to new template in different workspace
        // Note: Clauses don't have individual pages, they only have a landing page
        const templatePath = paths.templates.document;

        navigate(
          templatePath.replace(':teamId', clonedTeamId).replace(':templateId', clonedId).replace(':clauseId', clonedId)
        );
      } else {
        notifySuccess({ message: 'Template cloned successfully' });
        onSuccess();
      }
      onClose();
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to clone template' });
      setIsLoading(false);
    },
    refetchQueries: [
      {
        query: queriesV2.getDocumentTemplates,
        variables: {
          teamId: targetTeamId,
          templateType: templateType === 'clause' ? 'clause' : 'agreement',
        },
      },
    ],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      notifyError({ message: 'Please enter a name for the cloned template' });
      return;
    }

    setIsLoading(true);

    cloneTemplate({
      variables: {
        documentTemplateId: templateId,
        targetTeamId: targetTeamId !== sourceTeamId ? targetTeamId : null,
        name: name.trim(),
        folderId: selectedFolderId,
        // Only override when this is a docx-native template (where restricted
        // editing applies); otherwise inherit the source config untouched.
        ...(sourceIsDocxNative ? { restrictedEditing } : {}),
      },
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const validTeams = teamsData?.cloneTargetTeams || [];
  const showFolderSelector = targetTeamId; // Show folder selector for all workspaces
  const availableFolders = foldersData?.getTemplateFolders || [];

  const teamOptions = useMemo(() =>
    [...validTeams]
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
      .map((t: any) => ({
        value: t.id,
        label: t.id === sourceTeamId ? `${t.name} (Current)` : t.name,
      })),
    [validTeams, sourceTeamId]
  );

  const typeLabel = templateType === 'clause' ? 'Clause' : 'Template';

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isLoading}
      title={
        <FormattedMessage
          id="title.clone-template"
          defaultMessage={`Clone ${typeLabel}`}
          values={{ type: typeLabel }}
        />
      }
    >
      <Form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          {/* Name input */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="label.template-name" defaultMessage="Name" />
            </Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={formatMessage({
                id: 'placeholder.template-name',
                defaultMessage: 'Enter template name',
              })}
              required
              disabled={isLoading}
              autoFocus
            />
          </Form.Group>

          {/* Workspace selector */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="label.workspace" defaultMessage="Workspace" />
            </Form.Label>
            {teamsLoading ? (
              <div className="text-muted">
                <FormattedMessage id="message.loading-workspaces" defaultMessage="Loading workspaces..." />
              </div>
            ) : validTeams.length === 0 ? (
              <div className="text-muted">
                <FormattedMessage id="message.no-workspaces" defaultMessage="No workspaces available" />
              </div>
            ) : (
              <>
                <Select
                  options={teamOptions}
                  value={teamOptions.find((o: any) => o.value === targetTeamId) || null}
                  onChange={(option: any) => {
                    setTargetTeamId(option ? option.value : sourceTeamId);
                    setSelectedFolderId(null);
                  }}
                  isDisabled={isLoading || validTeams.length <= 1}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                />
                {validTeams.length > 1 && (
                  <Form.Text className="text-muted">
                    <FormattedMessage
                      id="help.workspace-selection"
                      defaultMessage="Available workspaces under the same ownership"
                    />
                  </Form.Text>
                )}
              </>
            )}
          </Form.Group>

          {/* Folder selector */}
          {showFolderSelector && (
            <Form.Group>
              <Form.Label>
                <FormattedMessage id="label.destination-folder" defaultMessage="Destination Folder" />
              </Form.Label>
              {foldersLoading ? (
                <div className="text-muted">
                  <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <>
                  <FolderTreeSelector
                    folders={availableFolders}
                    selectedFolderId={selectedFolderId}
                    onSelectionChange={setSelectedFolderId}
                    context={templateType === 'clause' ? 'clauses' : 'templates'}
                    disabled={isLoading}
                  />
                  <Form.Text className="text-muted mt-2">
                    <FormattedMessage
                      id="help.folder-selection"
                      defaultMessage="Choose the destination folder for this {type}. Select 'All {Type}s (Root)' to place it at the top level."
                      values={{
                        type: templateType === 'clause' ? 'clause' : 'template',
                        Type: templateType === 'clause' ? 'Clause' : 'Template',
                      }}
                    />
                  </Form.Text>
                </>
              )}
            </Form.Group>
          )}
        </div>

        {/* Restricted editing — docx-native templates only. Same slider + tooltip
            UI as the "Import from Word" modal. Defaults to the source template's
            setting; toggle off to clone as fully editable. */}
        {sourceIsDocxNative && templateType === 'template' && (
          <FormSection>
            <ToggleField
              messageId={AddNewTemplateRestrictedEditing.id}
              defaultMessage={AddNewTemplateRestrictedEditing.defaultMessage}
              tooltip={AddNewTemplateRestrictedEditing.TOOLTIP_MESSAGE}
              tooltipTestId="clone-restricted-editing-tooltip"
              checked={restrictedEditing}
              onChange={setRestrictedEditing}
              disabled={isLoading}
            />
          </FormSection>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>
          <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isLoading}>
            <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading || !name.trim() || teamsLoading}>
            {isLoading ? (
              <FormattedMessage id="cta.cloning" defaultMessage="Cloning..." />
            ) : (
              <TitleFormattedMessage id="cta.clone" defaultMessage="Clone" />
            )}
          </Button>
        </div>
      </Form>
    </PropdocsModal>
  );
};

export default CloneTemplateModal;
